import { Box, Button, HStack, Heading, Text, VStack } from 'native-base'
import { useCallback, useContext } from 'react'

import { useGateValue } from '@statsig/react-bindings'

import { Icon } from '@consumer/ui'
import PlusSVG from '../assets/PlusSVG'
import { AuthContext } from '../context/AuthContext'
import i18n from '../i18n'
import { IEditAddressProps, ToggleModalFuncProps } from '../context/OverlayContext'
import { Order } from '@consumer/graphql'
import { useScreenSize } from '../utils/displayUtils'
import { STATSIG_FEATURE_GATES } from '@consumer/shared'
import { isDelivered, isMisdeliveredWrongLocation } from '@consumer/shared'
import { colors } from '../utils/theme/configureTheme'
import { InstructionsRender } from './Instructions/Common/InstructionsRender'
import BusinessHoursDisplay from './TrackingPage/AddressInfo/BusinessHoursDisplay'
import DeliveryInstructions from './TrackingPage/AddressInfo/DeliveryInstructions'
import { TrackAnotherPackage } from './TrackingPage/Common/TrackAnotherPackage'

interface IDeliveryAddressProps {
  order: Order
  editAddress?: ({ clearAddressFields, locationUpdate, useLocationUpdate }: IEditAddressProps) => void
  addInstructions?: () => void
  styleProps?: object
  deliveryInstructionsBtnStyleProps?: object
  instructionsRenderContainerStyleProps?: object
  toggleMap?: () => void
  showViewPackageLocationButton?: boolean
  showViewAddressButton?: boolean
  toggleModal?: ({ showModal, auth_flow }: ToggleModalFuncProps) => void
  // Full variant props
  variant?: 'default' | 'full'
  onEditBusinessHours?: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onBackToSearch?: (e: any) => void
  hasProofOfServicePhotosWithLocation: boolean
}

export const DeliveryAddress = ({
  editAddress,
  addInstructions,
  order,
  toggleMap,
  styleProps,
  deliveryInstructionsBtnStyleProps,
  instructionsRenderContainerStyleProps,
  showViewPackageLocationButton = true,
  showViewAddressButton = true,
  toggleModal,
  variant = 'default',
  onEditBusinessHours,
  onBackToSearch,
  hasProofOfServicePhotosWithLocation = false,
}: IDeliveryAddressProps) => {
  const canEdit = !!(
    order?.address?.preferredLocation ||
    order?.address?.preferredPickupLocation ||
    order?.address?.instructions ||
    order?.address?.pickupInstructions ||
    order?.address?.securityCode ||
    order?.address?.attributes?.length
  )
  const isFullVariant = variant === 'full'
  const showPkgLocationBtnMisdeliveredWrongLocation = useGateValue(
    STATSIG_FEATURE_GATES.cwa_show_pkg_location_btn_misdelivered_wrong_location
  )
  const isDeliveredOrMisdeliveredWithPodLocation: boolean = isFullVariant
    ? isDelivered(order) ||
      (isMisdeliveredWrongLocation(order) &&
        hasProofOfServicePhotosWithLocation &&
        showPkgLocationBtnMisdeliveredWrongLocation)
    : isDelivered(order)
  const hasPiiFields = order?.address?.name && order?.address?.street
  const { firebaseUser } = useContext(AuthContext)
  const screenSize = useScreenSize()

  const EditButton = useCallback(() => {
    const showEditButton =
      order?.instructionEditsAllowed &&
      order?.isOrderReroutableByConsumer &&
      order?.isOrderReroutable &&
      !order?.rerouteBlockedByClient &&
      (firebaseUser ? order?.authedUserOwnsOrder : true)
    const showPackageLocationButton =
      showViewPackageLocationButton &&
      isDeliveredOrMisdeliveredWithPodLocation &&
      order?.instructionEditsAllowed &&
      !order?.rerouteBlockedByClient &&
      (firebaseUser ? order?.authedUserOwnsOrder : true)
    const showViewAddress =
      showViewAddressButton && order?.instructionEditsAllowed && !order?.rerouteBlockedByClient && !firebaseUser

    return showEditButton ? (
      <Button
        size="sm"
        width={screenSize.width <= 650 ? '100%' : '113px'}
        height="32px"
        borderRadius={100}
        bgColor="black"
        mt={screenSize.width <= 650 ? 2 : 0}
        onPress={() => {
          if (
            toggleModal &&
            !!order?.addressCorrectionAllowed &&
            !order?.address?.consumerSpecifiedAddressNormalization
          ) {
            toggleModal({ showModal: true, auth_flow: 'permanent_address_update' })
            return
          }

          if (editAddress) {
            editAddress({
              clearAddressFields: false,
              locationUpdate: null,
              useLocationUpdate: false,
              toggleMap: true,
            })
            return
          }

          return
        }}
      >
        {hasPiiFields ? i18n.t('common:EDIT') : 'View Address'}
      </Button>
    ) : showPackageLocationButton ? (
      <Button
        size="sm"
        width={screenSize.width <= 650 ? '100%' : '160px'}
        borderRadius={100}
        bgColor={colors.brand.vermillion}
        mt={screenSize.width <= 650 ? 2 : 0}
        onPress={() => (toggleMap ? toggleMap() : null)}
        flexWrap={'wrap'}
        maxHeight={'40px'}
      >
        <Box
          flexWrap={'wrap'}
          maxWidth={screenSize.width <= 650 ? '100%' : '80%'}
          alignSelf={'center'}
          paddingY={screenSize.width <= 650 ? 2 : 0}
          alignItems={'center'}
        >
          <Text color={'white'} textAlign="center" flexWrap="wrap" fontSize={14} lineHeight={12}>
            View Package Location
          </Text>
        </Box>
      </Button>
    ) : showViewAddress ? (
      <Button
        size="sm"
        width={screenSize.width <= 650 ? '100%' : '113px'}
        height="32px"
        borderRadius={100}
        bgColor="black"
        mt={screenSize.width <= 650 ? 2 : 0}
        onPress={() => (toggleModal ? toggleModal({ showModal: true, auth_flow: 'general' }) : null)}
      >
        View Address
      </Button>
    ) : null
  }, [
    order,
    firebaseUser,
    screenSize,
    editAddress,
    showViewPackageLocationButton,
    isDeliveredOrMisdeliveredWithPodLocation,
    hasPiiFields,
    toggleMap,
    showViewAddressButton,
    toggleModal,
  ])

  // Render instructions section based on variant
  const renderInstructions = () => {
    if (!order?.instructionEditsAllowed) return null

    if (isFullVariant) {
      return <DeliveryInstructions order={order} canEdit={canEdit} onEdit={addInstructions} />
    }

    return (
      <VStack pt={3} pb={2}>
        <Heading fontSize={10} fontWeight={'700'} fontFamily={'bodyBold'}>
          DELIVERY INSTRUCTIONS
        </Heading>
        <VStack style={{ ...instructionsRenderContainerStyleProps }}>
          {hasPiiFields && <InstructionsRender order={order as Order} />}
        </VStack>
        <Button
          onPress={() => addInstructions?.()}
          size="sm"
          backgroundColor={'transparent'}
          width={'100%'}
          style={{ ...deliveryInstructionsBtnStyleProps }}
        >
          <HStack alignItems={'center'}>
            {canEdit ? <Icon name="pencilSimple" /> : <PlusSVG />}
            <Text ml={1}>{canEdit ? 'Update Instructions' : 'Add Additional Instructions'}</Text>
          </HStack>
        </Button>
      </VStack>
    )
  }

  // Container component based on variant
  const Container = isFullVariant ? VStack : Box
  const containerProps = isFullVariant
    ? {
        style: {
          backgroundColor: 'white',
          borderRadius: 24,
          marginTop: -40,
          ...styleProps,
        },
        px: 2,
        py: 3,
        space: 3,
      }
    : {
        style: {
          backgroundColor: 'white',
          paddingTop: 18,
          paddingHorizontal: 21,
          borderRadius: 24,
          marginTop: -40,
          ...styleProps,
        },
      }

  return (
    <Container {...containerProps}>
      <HStack
        justifyContent={'space-between'}
        px={isFullVariant ? 3 : 0}
        pb={isFullVariant ? 0 : 3}
        borderBottomWidth={isFullVariant ? 0 : '1px'}
        borderBottomColor={isFullVariant ? undefined : '#ECECE5'}
        flexDirection={screenSize.width <= 650 ? 'column' : 'row'}
        space={screenSize.width <= 650 ? 3 : 0}
      >
        <VStack>
          <Heading fontSize={10} fontWeight={'700'} fontFamily={'bodyBold'}>
            DELIVERING TO:
          </Heading>
          {hasPiiFields && (
            <VStack>
              <Text fontSize={14} fontWeight={'400'}>
                {order?.address?.name}
              </Text>
              <Text fontSize={14} fontWeight={'400'}>
                {order?.address?.street}
              </Text>
              {order?.address?.apartment && (
                <Text fontSize={14} fontWeight={'400'}>
                  {order?.address?.apartment}
                </Text>
              )}
            </VStack>
          )}
          <Text fontSize={14} fontWeight={'400'}>
            {order?.address?.city}, {order?.address?.state} {order?.address?.zipCode}
          </Text>
        </VStack>
        <EditButton />
      </HStack>
      {renderInstructions()}
      {isFullVariant && order?.address?.hours?.enabled && (
        <BusinessHoursDisplay
          addressHours={order.address.hours}
          normalizedAddressId={order.address.normalizedAddressId || ''}
          orderId={order.orderId}
          trackingId={order?.packages?.[0]?.trackingId}
          onEdit={onEditBusinessHours}
          canEdit={!!order?.address?.hours?.dayHours}
        />
      )}
      {isFullVariant && onBackToSearch && (
        <Box pt={1} display="flex" justifyContent="center" alignItems="center">
          <TrackAnotherPackage onBackToSearch={onBackToSearch} />
        </Box>
      )}
    </Container>
  )
}
